import { Analytics } from '@cobliteam/analytics';
import type { Driver } from '@cobliteam/api-lib';
import type { IdentificationTokenV2 } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useIdentificationToken, useIdentifierAssociations } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import { Tooltip } from '@cobliteam/ui/components';
import Banner, { BannerText, BannerTitle } from '@cobliteam/ui/components/Banner';
import BannerContent from '@cobliteam/ui/components/InfoBanner/components/BannerContent';
import { FormControl, FormField, FormItem, FormLabel, FormMessage, SelectVirtualized, SelectVirtualizedTrigger } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import PopoverIButtonIdentifier from '../../../Popover/PopoverIButtonIdentifier';
import type { FormValues } from '../validationSchema';

interface DriverIdentifierInputProps {
  allDriversData: Driver[];
  form: UseFormReturn<FormValues>;
}

interface SelectOption {
  value: string;
  label: string;
  type?: string;
  fixed?: boolean;
  tokenId?: string;
  driverId?: string;
  isExternal?: boolean;
  tokenAlias?: string;
}

const DriverIdentifierInput = ({ allDriversData, form }: DriverIdentifierInputProps) => {
  const { t } = useTranslation('drivers');
  const { getIdentificationTokens } = useIdentifierAssociations.useIdentificationToken();
  const { data: identificationTokens, isLoading } = getIdentificationTokens();

  const { data: associations } = useIdentificationToken().getTokenAssociationsV2();
  const associatedTokens = associations?.associations.map((association) => association.tokenAlias);

  const [showBanner, setShowBanner] = useState(false);
  const initialSelectedToken = useRef(form.getValues('identificationToken')?.tokenAlias);

  const defaultOption: SelectOption = {
    value: '__none__',
    label: t('driverIdentifier.none'),
    type: undefined,
    fixed: true,
    driverId: undefined,
    tokenId: undefined,
    isExternal: undefined,
    tokenAlias: undefined,
  };

  const tokenOptions: SelectOption[] = (
    identificationTokens?.map((token) => {
      const alias = token.alias ?? '';
      return {
        value: alias,
        tokenId: token.id,
        label: selectedOptionLabel(token),
        type: token.type,
        driverId: token.driverId || undefined,
        isExternal: token.isExternal,
        tokenAlias: token.isExternal ? alias : alias.toUpperCase(),
      };
    }) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));

  const options = [defaultOption, ...tokenOptions];

  const CustomOptionElement = ({ isSelected, option }: { isSelected: boolean; option: SelectOption }) => {
    const isExternal = option.isExternal;
    const isNoneOption = option.value === '__none__';

    if (associatedTokens?.includes(option.value)) {
      const findDriverAssociated = associations?.associations.find((a) => {
        if (isExternal) {
          return a.tokenAlias === option.tokenAlias;
        }
        return a.tokenAlias?.toUpperCase() === option.tokenAlias;
      });
      const driverAssociated = allDriversData.find((a) => a.id === findDriverAssociated?.driverId);
      const identificationOf = `Identificador de ${driverAssociated?.name}`;

      const identifierAlias = isExternal ? option.value : option.value.toUpperCase();

      return (
        <Tooltip content={identificationOf} asChild>
          <div className='flex h-10 w-full items-center justify-between' data-testid='select-option'>
            <div className='flex items-center gap-2'>
              {!isNoneOption && <Icon name={isExternal ? 'credit-card' : 'cb-ibutton'} />}
              {isNoneOption ? t('driverIdentifier.none') : identifierAlias}
            </div>
            <div className='flex'>
              {isSelected && !isNoneOption && <Icon name='check' color='success' className='shrink-0' />}
              <Icon name='link' className={'ml-2 shrink-0'} />
            </div>
          </div>
        </Tooltip>
      );
    }

    return (
      <div className='flex h-10 w-full items-center justify-between' data-testid='select-option'>
        <div className='flex items-center gap-2'>
          {!isNoneOption && <Icon name={isExternal ? 'credit-card' : 'cb-ibutton'} />}
          {isNoneOption ? t('driverIdentifier.none') : isExternal ? option.value : option.value.toUpperCase()}
        </div>
        {isSelected && !isNoneOption && <Icon name='check' color='success' className='shrink-0' />}
      </div>
    );
  };

  return (
    <div>
      <FormField name='identificationToken'>
        {({ field }) => {
          const handleIdentifierChange = (option: SelectOption) => {
            const { type, value: rawValue, tokenId, driverId } = option;
            const value = rawValue === '__none__' ? '' : rawValue;

            const isExternal = type === 'rfid';
            const selectedValue = {
              tokenAlias: isExternal ? value : value.toUpperCase(),
              tokenId,
              isExternal,
              driverId,
            };

            field.onChange(selectedValue);
            setShowBanner(Boolean(selectedValue.tokenAlias !== initialSelectedToken.current && associatedTokens?.includes(selectedValue.tokenAlias)));

            Analytics.track('Identifier Item Clicked', {
              alias: value.length === 0 ? undefined : value,
              isExternal,
            });
          };

          const selectedOption =
            tokenOptions.find((option) => {
              const isExternal = option.type === 'rfid';

              if (isExternal) {
                return field.value?.tokenAlias?.toUpperCase() === option.value.toUpperCase();
              }

              return field.value?.tokenAlias?.toUpperCase() === option.value.toUpperCase();
            }) ?? defaultOption;

          return (
            <FormItem className='mt-3 w-full space-y-1'>
              <div className='flex items-center'>
                <FormLabel>Identificador</FormLabel>
                <PopoverIButtonIdentifier portal={false} />
              </div>
              <FormControl>
                <SelectVirtualized<SelectOption>
                  portal={false}
                  options={options}
                  optionHeight={40}
                  height={300}
                  emptyIndicator={'O código digitado não existe.'}
                  value={selectedOption}
                  selectItem={({ isSelected, option }) => <CustomOptionElement isSelected={isSelected} option={option} />}
                  onValueChange={(option) => handleIdentifierChange(option as SelectOption)}
                >
                  <SelectVirtualizedTrigger disabled={isLoading} data-testid={'select-identifier'} placeholder='Selecionar identificador' optionSelected={selectedOption} />
                </SelectVirtualized>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      </FormField>
      {showBanner && <WarningBanner />}
    </div>
  );
};

const WarningBanner = () => {
  return (
    <Banner color='warning'>
      <BannerContent className='!items-start flex flex-col'>
        <BannerTitle className='!text-warning-11'>Este motorista já está vinculado a outro identificador.</BannerTitle>
        <BannerText color='warning'>Ao continuar, a vinculação será desfeita e será substituída pela vinculação com este motorista.</BannerText>
      </BannerContent>
    </Banner>
  );
};

const selectedOptionLabel = (token: IdentificationTokenV2): string => {
  const isExternal = token.isExternal;
  const isRfid = token.type === 'rfid';
  const alias = token.alias ?? '';
  const tokenIdPayload = token.id.substring(2, 14);
  const revertedId = revertHex2by2(token.id);
  const { tokenInDecimal } = getTokenIDs(token.id);
  const { tokenInDecimal: revertedTokenInDecimal } = getTokenIDs(revertedId);
  const identifierAlias = isExternal ? alias : alias.toUpperCase();
  const rfidLabel = alias === tokenIdPayload ? `${tokenInDecimal} (${revertedTokenInDecimal})` : identifierAlias;

  return isExternal && isRfid ? rfidLabel : identifierAlias;
};

const getTokenIDs = (id: string) => {
  const tokenInDecimal = Number.parseInt(id.substring(2, 14), 16);
  const tokenInHex = tokenInDecimal.toString(16); // This removes 0s padding

  return {
    tokenInDecimal,
    tokenInHex,
  };
};

const revertHex2by2 = (hex: string) => {
  const sanitized = hex.startsWith('0x') ? hex.slice(2) : hex;

  if (sanitized.length === 0) return '';

  const padded = sanitized.length % 2 === 0 ? sanitized : `0${sanitized}`;
  const reversed = (padded.match(/.{2}/g) || []).reverse().join('');

  return reversed;
};

export default DriverIdentifierInput;
